import React from 'react';
import { Container } from 'reactstrap';
import { TJoint } from '../../../lib/types';
import { useMutation, useQuery } from '@apollo/client';
import { GET_CERTIFICATE_DETAILS } from '../../../graph-queries/tar/certificate';
import {
  CREATE_JOINT,
  GET_JOINTS_BY_CERTIFICATE_ID,
  REMOVE_JOINT,
  UPDATE_JOINT,
} from '../../../graph-queries/tar/joint';
import { ColumnDef } from '@tanstack/react-table';
import AspectsTable from '../AspectsTable/AspectsTable';
import { useAlertText } from '../AspectsTable/util';
import JointForm from '../JointForm/JointForm';
import {
  useCurrentUsername,
  useHasMaintenanceAdminAccess,
  useHasMaintenanceUserAccess,
} from '../../../lib/util/auth';
import { Certificate, CertificateStatusEnum } from '../../../graph-queries/__generated__/graphql';
import JointViewForm from '../JointForm/JointViewForm';

export const columns: ColumnDef<TJoint>[] = [
  {
    header: 'Joint Number',
    accessorKey: 'jointNumber',
    sortingFn: 'alphanumeric',
  },
  {
    header: 'Tag Id',
    accessorKey: 'tagId',
  },
  {
    header: 'P&ID No',
    accessorKey: 'pIdNo',
  },
  {
    header: 'Additional Details',
    accessorKey: 'additionalDetails',
  },
];

type Props = {
  certificateId: number;
};

const CertificateJointsTable: React.FC<Props> = ({ certificateId }) => {
  const currentUsername = useCurrentUsername();
  const hasMaintenanceUserAccess = useHasMaintenanceUserAccess();
  const hasMaintenanceAdminAccess = useHasMaintenanceAdminAccess();
  const { data, loading } = useQuery(GET_JOINTS_BY_CERTIFICATE_ID, {
    variables: { certificateId },
  });

  const { data: certificateData } = useQuery(GET_CERTIFICATE_DETAILS, {
    variables: { certificateId },
  });

  const certificateDetails = certificateData?.certificateDetails as Certificate;
  const certificateType = certificateDetails?.type || '';

  const { alertText, setAlertText } = useAlertText();

  const isLoading = loading;

  const [createJoint, { loading: isCreating }] = useMutation(CREATE_JOINT, {
    refetchQueries: [GET_JOINTS_BY_CERTIFICATE_ID],
    awaitRefetchQueries: true,
  });
  const [updateJoint, { loading: isUpdating }] = useMutation(UPDATE_JOINT, {
    refetchQueries: [GET_JOINTS_BY_CERTIFICATE_ID],
    awaitRefetchQueries: true,
  });

  const [removeJoint, { loading: isRemoving }] = useMutation(REMOVE_JOINT, {
    refetchQueries: [GET_JOINTS_BY_CERTIFICATE_ID],
    awaitRefetchQueries: true,
  });

  const onAddSubmit = (certificate: TJoint) => {
    const { updatedAt, ...input } = certificate;
    return createJoint({ variables: { input } }).then(() =>
      setAlertText('Joint successfully added')
    );
  };
  const onEditSubmit = (certificate: TJoint) => {
    const { updatedAt, ...input } = certificate;
    return updateJoint({ variables: { input } }).then(() =>
      setAlertText('Joint successfully updated')
    );
  };

  const onDuplicationSubmit = (certificate: TJoint) => {
    const { updatedAt, ...input } = certificate;
    return createJoint({ variables: { input } }).then(() =>
      setAlertText('Joint successfully added')
    );
  };

  const onRemoveSubmit = (selection: TJoint[]) => {
    return Promise.all(
      selection.map((joint) => removeJoint({ variables: { jointId: joint.jointId } }))
    ).then(() => setAlertText(`Joint successfully deleted`));
  };

  return (
    <Container>
      {loading ? (
        'Loading...'
      ) : (
        <AspectsTable<TJoint>
          certificateType={certificateType}
          hideSelectionColumn
          title=" "
          aspectName="joint"
          data={(data?.jointsByCertificateId as unknown as TJoint[]) || []}
          columns={columns}
          alertText={alertText}
          onRemoveSubmit={onRemoveSubmit}
          defaultSortingColumn="jointNumber"
          onJointReuse={onDuplicationSubmit}
          viewAllUrl={`/maintenance/certificates/${certificateId}/joints?type=${certificateType}`}
          renderAddForm={(onClose) => (
            <JointForm
              certificateType={certificateType}
              onSubmit={onAddSubmit}
              onClose={onClose}
              type={'add'}
              isLoading={isLoading}
            />
          )}
          renderEditForm={(onClose, defaultValue) => (
            <JointForm
              certificateType={certificateType}
              defaultValues={defaultValue}
              onSubmit={onEditSubmit}
              onClose={onClose}
              type={'edit'}
              isLoading={isLoading}
            />
          )}
          renderDuplicationForm={(onClose, defaultValue) => (
            <JointForm
              certificateType={certificateType}
              defaultValues={defaultValue}
              onSubmit={onDuplicationSubmit}
              onClose={onClose}
              type={'add'}
              isLoading={isLoading}
            />
          )}
          creationDisabled={
            !hasMaintenanceUserAccess ||
            certificateDetails?.status === CertificateStatusEnum.Approved ||
            certificateDetails?.status === CertificateStatusEnum.Complete
          }
          getRowMutationDisabled={(row) => {
            const isCertificateApprovedOrComplete =
              certificateDetails?.status === CertificateStatusEnum.Approved ||
              certificateDetails?.status === CertificateStatusEnum.Complete;
            return isCertificateApprovedOrComplete || !hasMaintenanceUserAccess;
          }}
          getRowDeletionDisabled={(row) => {
            const isCertificateApprovedOrComplete =
              certificateDetails?.status === CertificateStatusEnum.Approved ||
              certificateDetails?.status === CertificateStatusEnum.Complete;
            return isCertificateApprovedOrComplete || !hasMaintenanceUserAccess;
          }}
          searchEnabled={true}
          searchPlaceholderText="Search for joints..."
          isLoading={isLoading}
        />
      )}
    </Container>
  );
};

export default CertificateJointsTable;
